/**
 * Search Toggle: desktop/tablet shows an inline search field with real-time
 * inline autocomplete (ghost text); mobile opens the off-canvas menu in search
 * mode. Clicking anywhere outside the open field restores the header.
 */
const MOBILE_BREAKPOINT = 768;
const SUGGEST_DEBOUNCE_MS = 140;
const SUGGEST_MIN_CHARS = 2;
// Slightly longer than the 120ms opacity/visibility expand on `.header__search-inline`.
const DESKTOP_FOCUS_FALLBACK_MS = 160;

const SearchToggle = {
    suggestion: '',

    init() {
        this.headerToggle = document.querySelector('.header__actions [data-search-toggle]');
        this.navMenu = document.querySelector('[data-nav-menu]');
        this.searchInline = document.querySelector('[data-search-inline]');
        this.form = this.searchInline?.querySelector('[data-search-form]');
        this.bar = this.searchInline?.querySelector('.header__search-bar');
        this.input = this.searchInline?.querySelector('[data-search-input]');
        this.typedEl = this.searchInline?.querySelector('[data-search-typed]');
        this.suggestionEl = this.searchInline?.querySelector('[data-search-suggestion]');
        this.controls = this.searchInline?.querySelector('[data-search-controls]');
        this.clearBtn = this.searchInline?.querySelector('[data-search-clear]');
        this.header = document.querySelector('.header__content');

        if (!this.headerToggle || !this.navMenu || !this.searchInline || !this.input) return;

        this.headerToggle.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (window.innerWidth < MOBILE_BREAKPOINT) {
                this.openMobileSearch();
            } else {
                this.isOpen() ? this.closeSearch() : this.openSearch();
            }
        });

        this.input.addEventListener('input', () => this.onInput());
        this.input.addEventListener('keydown', (e) => this.onKeydown(e));

        this.clearBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            this.input.value = '';
            this.onInput();
            this.input.focus();
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') this.closeSearch();
        });

        // Click anywhere outside the open field restores the header.
        document.addEventListener('click', (e) => {
            if (!this.isOpen()) return;
            if (this.searchInline.contains(e.target)) return;
            this.closeSearch();
        });

        this.form?.addEventListener('submit', (e) => {
            if (this.input.value.trim() === '') {
                e.preventDefault();
                this.input.focus();
            }
        });
    },

    isMobile: () => window.innerWidth < MOBILE_BREAKPOINT,

    isOpen() {
        return this.searchInline?.hidden === false;
    },

    openMobileSearch() {
        const offCanvas = document.querySelector('.off-canvas--menu');
        if (!offCanvas) return;

        offCanvas.querySelectorAll('.off-canvas__nav-item').forEach(item => {
            item.classList.add('is-collapsed');
        });

        if (!offCanvas.classList.contains('off-canvas--visible')) {
            offCanvas.classList.add('off-canvas--visible');
            document.body.classList.add('off-canvas-menu-open', 'off-canvas-opened-by-search');
        }

        const mobileInput = offCanvas.querySelector('[data-mobile-search-input]');
        mobileInput?.focus();
        setTimeout(() => mobileInput?.focus(), 300);
    },

    openSearch() {
        this.navMenu.hidden = true;
        this.searchInline.hidden = false;
        document.querySelectorAll('[data-search-toggle]').forEach(t => t.setAttribute('aria-expanded', 'true'));
        this.header?.classList.add('is-search-active');
        document.body.classList.add('search-active');
        this.focusInputWhenVisible();
    },

    cancelPendingFocus() {
        if (this.searchInline && this._onSearchTransitionEnd) {
            this.searchInline.removeEventListener('transitionend', this._onSearchTransitionEnd);
        }
        this._onSearchTransitionEnd = null;
        clearTimeout(this._focusTimer);
        this._focusTimer = null;
    },

    /**
     * Desktop expand starts from [hidden] (display:none / visibility:hidden).
     * Browsers ignore focus() until the field is visible, and hiding the
     * focused toggle via `.is-search-active` moves focus to <body>.
     */
    focusInputWhenVisible() {
        const input = this.input;
        const panel = this.searchInline;
        if (!input || !panel) return;

        this.cancelPendingFocus();

        const focusInput = () => {
            this.cancelPendingFocus();
            if (!this.isOpen()) return;
            input.focus();
        };

        this._onSearchTransitionEnd = (event) => {
            if (event.target !== panel) return;
            if (event.propertyName !== 'opacity' && event.propertyName !== 'visibility') return;
            focusInput();
        };
        panel.addEventListener('transitionend', this._onSearchTransitionEnd);
        this._focusTimer = setTimeout(focusInput, DESKTOP_FOCUS_FALLBACK_MS);
    },

    closeSearch() {
        if (!this.navMenu || !this.searchInline) return;

        this.cancelPendingFocus();
        this.navMenu.hidden = false;
        this.searchInline.hidden = true;
        document.querySelectorAll('[data-search-toggle]').forEach(t => t.setAttribute('aria-expanded', 'false'));
        this.header?.classList.remove('is-search-active');
        document.body.classList.remove('search-active');
        if (this.input) this.input.value = '';
        this.resetTypingState();
    },

    resetTypingState() {
        this.suggestion = '';
        this.bar?.classList.remove('is-typing');
        if (this.controls) this.controls.hidden = true;
        if (this.typedEl) this.typedEl.textContent = '';
        if (this.suggestionEl) this.suggestionEl.textContent = '';
    },

    onInput() {
        const value = this.input.value;
        const hasValue = value.trim() !== '';

        this.bar?.classList.toggle('is-typing', hasValue);
        if (this.controls) this.controls.hidden = !hasValue;

        // Ghost suggestion no longer valid once the input changed.
        this.setSuggestion('');

        if (!hasValue || value.length < SUGGEST_MIN_CHARS) return;
        this.debouncedFetch(value);
    },

    setSuggestion(fullSuggestion) {
        const value = this.input.value;
        let tail = '';
        if (
            fullSuggestion &&
            fullSuggestion.length > value.length &&
            fullSuggestion.toLowerCase().startsWith(value.toLowerCase())
        ) {
            tail = fullSuggestion.slice(value.length);
        }
        this.suggestion = tail ? value + tail : '';
        if (this.typedEl) this.typedEl.textContent = tail ? value : '';
        if (this.suggestionEl) this.suggestionEl.textContent = tail;
    },

    acceptSuggestion() {
        if (!this.suggestion) return false;
        this.input.value = this.suggestion;
        this.onInput();
        const end = this.input.value.length;
        this.input.setSelectionRange(end, end);
        return true;
    },

    onKeydown(e) {
        const atEnd = this.input.selectionStart === this.input.value.length &&
            this.input.selectionEnd === this.input.value.length;

        if (e.key === 'Tab' && this.suggestion) {
            e.preventDefault();
            this.acceptSuggestion();
            return;
        }

        if (e.key === 'ArrowRight' && atEnd && this.suggestion) {
            e.preventDefault();
            this.acceptSuggestion();
        }
    },

    debouncedFetch(value) {
        clearTimeout(this._suggestTimer);
        this._suggestTimer = setTimeout(() => this.fetchSuggestion(value), SUGGEST_DEBOUNCE_MS);
    },

    fetchSuggestion(query) {
        const cfg = window.pe_mp_search || {};
        const ajaxUrl = cfg.ajax_url || (typeof ajaxurl !== 'undefined' ? ajaxurl : '/wp-admin/admin-ajax.php');
        const params = new URLSearchParams({
            action: 'pe_mp_search_suggest',
            q: query,
            nonce: cfg.nonce || '',
        });

        fetch(`${ajaxUrl}?${params.toString()}`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
            .then((res) => res.json())
            .then((data) => {
                if (!data || !data.success) return;
                // Ignore stale responses: the input may have changed while in flight.
                if (this.input.value !== query) return;
                this.setSuggestion(data.data && data.data.suggestion ? data.data.suggestion : '');
            })
            .catch(() => {});
    },
};

export default SearchToggle;
